/** Popover-очередь уведомлений.
 *
 *  Структура соответствует фрейму «11 Notifications Dropdown» в .pen:
 *  - Header: заголовок + Mark all read + settings, под ним табы scope.
 *  - Body: список карточек (NotificationItem) либо empty-state.
 *  - Footer: статус WS + счётчик.
 */
import { type Component, For, Show, createMemo } from "solid-js";
import { A } from "@solidjs/router";
import { Archive, ArchiveX, Bell, CheckCheck, Settings as SettingsIcon } from "lucide-solid";
import { useI18n } from "~/i18n";
import { useNotifications } from "~/contexts/NotificationsProvider";
import { NotificationItem } from "~/components/layout/NotificationItem";
import type { Notification, NotificationScope } from "~/lib/notifications";

interface Props {
  onClose: () => void;
  onOpenDetail: (n: Notification) => void;
}

export const NotificationDropdown: Component<Props> = (props) => {
  const { t } = useI18n();
  const {
    items,
    summary,
    scope,
    setScope,
    showResolved,
    setShowResolved,
    pollingActive,
    markAllRead,
  } = useNotifications();

  const unreadByScope = (s: NotificationScope) => {
    const sum = summary();
    if (!sum) return 0;
    return s === "active" ? sum.unread_active : sum.unread_all;
  };

  // Индикатор привязан к реальному polling-fallback (дебаунс 5с в провайдере),
  // а не к сырому wsState — иначе короткие реконнекты мигают «Связь потеряна».
  const offline = createMemo(() => pollingActive());

  return (
    <div class="absolute right-0 top-full z-50 mt-2 w-[400px] overflow-hidden rounded-xl border border-border bg-surface text-ink shadow-xl">
      {/* Header */}
      <div class="flex flex-col gap-2.5 border-b border-border px-4 pb-3 pt-3.5">
        <div class="flex items-center justify-between gap-2">
          <span class="text-base font-bold text-ink">{t("notifications.title")}</span>
          <div class="flex items-center gap-1">
            <button
              type="button"
              onClick={() => void markAllRead()}
              class="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
            >
              <CheckCheck size={14} stroke-width={1.8} />
              <span>{t("notifications.markAllRead")}</span>
            </button>
            <button
              type="button"
              onClick={() => setShowResolved(!showResolved())}
              aria-label={showResolved() ? t("notifications.hideResolved") : t("notifications.showResolved")}
              title={showResolved() ? t("notifications.hideResolved") : t("notifications.showResolved")}
              class="inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors"
              classList={{
                "bg-primary-soft text-primary": showResolved(),
                "text-ink-muted hover:bg-surface-hover hover:text-ink": !showResolved(),
              }}
            >
              {showResolved() ? (
                <ArchiveX size={14} stroke-width={1.8} />
              ) : (
                <Archive size={14} stroke-width={1.8} />
              )}
            </button>
            <A
              href="/profile"
              onClick={() => props.onClose()}
              aria-label={t("notifications.openSettings")}
              class="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
            >
              <SettingsIcon size={14} stroke-width={1.8} />
            </A>
          </div>
        </div>

        {/* Tabs scope */}
        <div class="flex gap-1 rounded-lg bg-surface-3 p-1">
          <For each={["active", "all"] as const}>
            {(s) => (
              <button
                type="button"
                onClick={() => setScope(s)}
                class="flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors"
                classList={{
                  "bg-surface text-ink shadow-sm ring-1 ring-border-faint": scope() === s,
                  "text-ink-muted hover:text-ink": scope() !== s,
                }}
              >
                <span>{t(`notifications.scope.${s}`)}</span>
                <span
                  class="inline-flex h-4 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold"
                  classList={{
                    "bg-primary text-white": scope() === s,
                    "bg-neutral-soft text-ink-muted": scope() !== s,
                  }}
                >
                  {unreadByScope(s)}
                </span>
              </button>
            )}
          </For>
        </div>
      </div>

      {/* Body */}
      <div class="max-h-[60vh] overflow-auto">
        <Show
          when={items().length > 0}
          fallback={
            <div class="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
              <div class="flex h-14 w-14 items-center justify-center rounded-full bg-surface-3">
                <Bell size={26} class="text-ink-muted" stroke-width={1.6} />
              </div>
              <div class="text-base font-semibold text-ink">{t("notifications.empty")}</div>
              <div class="text-sm text-ink-muted">{t("notifications.emptyHint")}</div>
            </div>
          }
        >
          <ul>
            <For each={items()}>
              {(n) => (
                <li>
                  <NotificationItem
                    notification={n}
                    onClose={props.onClose}
                    onOpenDetail={props.onOpenDetail}
                  />
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>

      {/* Footer */}
      <div class="flex items-center justify-between border-t border-border-faint px-4 py-2.5 text-xs">
        <span
          classList={{
            "text-success font-medium": !offline(),
            "text-warning font-medium": offline(),
          }}
        >
          {offline() ? t("notifications.connection.offline") : t("notifications.connection.online")}
        </span>
        <span class="text-ink-muted">
          {t("notifications.footer.shown", { n: items().length })}
        </span>
      </div>
    </div>
  );
};
